import React, { useEffect, useState } from "react";
import { useScrollLock } from "../../../hooks/useScrollLock";
import {
  FiMusic,
  FiClock,
  FiStar,
  FiEdit,
  FiTrash2,
  FiPlus,
  FiX,
  FiMapPin,
  FiCalendar,
  FiExternalLink,
  FiShare2,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
} from "react-icons/fi";
import { FaRegCalendarAlt, FaTicketAlt, FaTimes } from "react-icons/fa";
import styled from "styled-components";

import {
  StatusBadge,
  PageHeader,
  Button,
  FormInput,
  FormGroup,
  FormLabel,
  FormSelect,
  FormTextarea,
  Modal,
  ModalClose,
  ModalContent,
  ModalHeader,
  ModalTitle,
  PageSection,
  PageTitle,
  ProductActions,
  ProductGrid,
  NewsCard,
  FilterTabs,
  FilterTab,
  NewsMeta,
  NewsType as AdminNewsType,
  NewsDate as AdminNewsDate,
  NewsBand,
  NewsExcerpt as AdminNewsExcerpt,
  NewsHeader,
  NewsTitle as AdminNewsTitle,
  Time,
  BadgeContainer,
  ModalOverlay,
  ConfirmBox,
  CloseBtn,
  ModalActions,
  Cancel,
  Confirm,
  Relative,
  EmptyState,
  PaginationContainer,
  PaginationButton,
  PaginationInfo,
  SearchContainer,
  SearchInput,
} from "./AdminNewsPage.styles";

import AdminContainer from "../../../components/utils/AdminContainer";
import { useDispatch, useSelector } from "react-redux";
import {
  createNews,
  deleteNews,
  editNews,
  getNews,
  toggleNewsStatus,
} from "../../../store/slices/news/asyncThunks";
import { selectNews } from "../../../store/slices/news/slice";
import { AnimatePresence, motion } from "framer-motion";
import {
  formatNewsDate,
  getNewsTypeIcon,
  getNewsTypeLabel,
} from "../../../utils/utils";
import { MdAccessTimeFilled } from "react-icons/md";
import { selectBandsDropdown } from "../../../store/slices/bands/slice";
import Loader from "../../../components/utils/Loader";
import { useNavigate } from "react-router-dom";
import { toast } from "../../../components/modern-ui/sonner";

const ITEMS_PER_PAGE = 12;

const AdminNewsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    data: newsData,
    total,
    page: currentPage,
    loading,
    createLoading,
    editLoading,
    deleteLoading,
    toggleLoading,
  } = useSelector(selectNews);

  const {
    data: bandsData,
    loading: bandsLoading,
    error: bandsError,
  } = useSelector(selectBandsDropdown);

  // Local state
  const [selectedNews, setSelectedNews] = useState(null);
  const [newsFilter, setNewsFilter] = useState("all");
  const [activeModal, setActiveModal] = useState(null);
  const [deleteNewsModal, setDeleteNewsModal] = useState(false);
  const [newsId, setNewsId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Image upload state
  const [newsImage, setNewsImage] = useState(null);
  const [newsImagePreview, setNewsImagePreview] = useState(null);

  useScrollLock(activeModal || deleteNewsModal);

  // Image handling functions
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        toast.error("Slika je prevelika. Maksimalna veličina je 5MB.");
        return;
      }

      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/webp",
        "image/gif",
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Nepodržan format slike. Dozvoljen: JPG, PNG, WebP, GIF");
        return;
      }

      setNewsImage(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setNewsImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setNewsImage(null);
    setNewsImagePreview(null);
  };

  // Helper function for date formatting
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().split("T")[0];
  };

  // Preview state - FIXED: use bandId consistently
  const [previewData, setPreviewData] = useState({
    title: "",
    bandId: "", // Fixed: was band, now bandId
    type: "",
    text: "",
    location: "",
    venue: "",
    time: "",
    date: "",
    externalLink: "",
    ticketLink: "",
    isFeatured: false,
    active: false,
  });

  // Load data on component mount
  useEffect(() => {
    dispatch(getNews({ page: 1, limit: ITEMS_PER_PAGE }));
  }, [dispatch]);

  // Function to load news for specific page
  const loadNews = (page) => {
    dispatch(
      getNews({
        page,
        limit: ITEMS_PER_PAGE,
      })
    );
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(total / ITEMS_PER_PAGE);
    if (newPage >= 1 && newPage <= totalPages && !loading) {
      loadNews(newPage);
    }
  };

  // Calculate total pages
  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (startPage > 1) {
        pageNumbers.push(1);
        if (startPage > 2) pageNumbers.push("...");
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < totalPages) {
        if (endPage < totalPages - 1) pageNumbers.push("...");
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  // Update preview when editing existing news - FIXED: properly handle bandId
  useEffect(() => {
    if (activeModal === "editNews" && selectedNews) {
      setPreviewData({
        title: selectedNews.title || "",
        bandId: selectedNews.bandId || "", // Fixed: properly set bandId
        type: selectedNews.type || "",
        text: selectedNews.text || "",
        location: selectedNews.location || "",
        venue: selectedNews.venue || "",
        time: selectedNews.time || "",
        externalLink: selectedNews.externalLink || "",
        ticketLink: selectedNews.ticketLink || "",
        date: formatDateForInput(selectedNews.date) || "",
        isFeatured: selectedNews.isFeatured || false,
        active: selectedNews.active || false,
      });

      // Set existing image if available
      if (selectedNews.newsImages && selectedNews.newsImages.length > 0) {
        const mainImage =
          selectedNews.newsImages.find((img) => img.isMain) ||
          selectedNews.newsImages[0];
        setNewsImagePreview(mainImage.url);
        setNewsImage(null); // No new file, just showing existing
      } else {
        setNewsImage(null);
        setNewsImagePreview(null);
      }
    } else if (activeModal === "addNews") {
      setPreviewData({
        title: "",
        bandId: "",
        type: "",
        text: "",
        location: "",
        venue: "",
        time: "",
        date: "",
        externalLink: "",
        ticketLink: "",
        isFeatured: false,
        active: false,
      });

      // Reset image state for new news
      setNewsImage(null);
      setNewsImagePreview(null);
    }
  }, [activeModal, selectedNews]);

  // Close modal function
  const closeModal = () => {
    setActiveModal(null);
    setSelectedNews(null);
    setPreviewData({
      title: "",
      bandId: "",
      type: "",
      text: "",
      location: "",
      venue: "",
      time: "",
      date: "",
      externalLink: "",
      ticketLink: "",
      isFeatured: false,
      active: false,
    });

    // Reset image state
    setNewsImage(null);
    setNewsImagePreview(null);
  };

  const openModal = (modalId) => {
    setActiveModal(modalId);
  };

  // Toggle news status
  const toggleNewsStatusHandler = (newsId, active) => {
    dispatch(toggleNewsStatus(newsId))
      .unwrap()
      .then(() => {
        dispatch(getNews({ page: 1, limit: ITEMS_PER_PAGE }));
        toast.success(
          `Uspešno ste ${
            active ? "uklonili iz objavljenih" : "objavili"
          }  novost.`
        );
      })
      .catch((err) => toast.error(err.message));
  };

  // Filter and search news on frontend
  const filteredNews = newsData
    .filter((n) => {
      // Apply news filter first
      if (newsFilter === "active") return n.active;
      if (newsFilter === "draft") return !n.active;
      if (newsFilter === "featured") return n.isFeatured;
      if (newsFilter !== "all" && n.type !== newsFilter) return false;
      return true;
    })
    .filter((n) => {
      // Apply search filter
      if (!searchTerm.trim()) return true;
      const searchLower = searchTerm.toLowerCase();
      const titleMatch = n.title?.toLowerCase().includes(searchLower) || false;
      const bandMatch =
        n.band?.name?.toLowerCase().includes(searchLower) || false;
      return titleMatch || bandMatch;
    });

  // Handle form changes - FIXED: consistent bandId handling
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    let fieldName = name;
    if (name === "newsTitle") fieldName = "title";
    else if (name === "newsBand") fieldName = "bandId"; // Fixed: map to bandId
    else if (name === "newsType") fieldName = "type";
    else if (name === "newsText") fieldName = "text";
    else if (name === "newsLocation") fieldName = "location";
    else if (name === "newsVenue") fieldName = "venue";
    else if (name === "newsTime") fieldName = "time";
    else if (name === "newsDate") fieldName = "date";
    else if (name === "newsExternalLink") fieldName = "externalLink";
    else if (name === "ticketLink") fieldName = "ticketLink";
    else if (name === "newsActive") fieldName = "active";
    else if (name === "newsIsFeatured") fieldName = "isFeatured";

    setPreviewData((prev) => ({
      ...prev,
      [fieldName]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle news submit - FIXED: simplified bandId handling with image support
  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();

    try {
      // Add text data
      formData.append("title", e.target.newsTitle.value);
      formData.append("bandId", parseInt(e.target.newsBand.value));
      formData.append("type", e.target.newsType.value);
      formData.append("text", e.target.newsText.value);
      formData.append("location", e.target.newsLocation.value || "");
      formData.append("venue", e.target.newsVenue.value || "");
      formData.append("externalLink", e.target.newsExternalLink.value || "");
      formData.append("ticketLink", e.target.ticketLink.value || "");
      formData.append("time", e.target.newsTime.value || "");
      formData.append("date", e.target.newsDate.value || "");
      formData.append("active", e.target.newsActive.checked);
      formData.append("isFeatured", e.target.newsIsFeatured.checked);

      // Add image if present
      if (newsImage) {
        formData.append("images", newsImage);
        console.log("Adding image to news:", newsImage.name);
      }

      if (activeModal === "addNews") {
        await dispatch(createNews(formData)).unwrap();
        // Refresh current page to show new news
        loadNews(currentPage);
      } else {
        await dispatch(
          editNews({ id: selectedNews.id, requestData: formData })
        ).unwrap();
      }

      closeModal();
      e.target.reset();
    } catch (error) {
      console.error("Error saving news:", error);
      toast.error(
        "Greška pri čuvanju novosti: " + (error.message || "Nepoznata greška")
      );
    }
  };

  const openEditNewsModal = (newsItem) => {
    setSelectedNews(newsItem);
    setActiveModal("editNews");
  };

  // Render preview component - FIXED: proper band finding by ID
  const renderPreview = () => {
    const TypeIcon = getNewsTypeIcon(previewData.type);
    const selectedBand = bandsData.find(
      (band) => band.id == previewData.bandId // Fixed: compare by ID
    );
    const bandName = selectedBand ? selectedBand.name : "";

    const hasContent = previewData.title || bandName || previewData.text;

    if (!hasContent) {
      return (
        <div
          style={{
            color: "#666",
            textAlign: "center",
            padding: "2rem",
            fontStyle: "italic",
          }}
        >
          Počnite da unosite podatke da vidite preview...
        </div>
      );
    }

    return (
      <PreviewNewsCard>
        <NewsImage $backgroundImage={newsImagePreview}>
          <NewsDate>{formatNewsDate(new Date())}</NewsDate>
          <NewsType type={previewData.type}>
            <TypeIcon size={14} />
            {getNewsTypeLabel(previewData.type)}
          </NewsType>
        </NewsImage>

        <NewsContent>
          {bandName && <BandTag>{bandName}</BandTag>}

          <NewsTitle>
            {previewData.title || "Unesite naslov novosti..."}
          </NewsTitle>

          {previewData.location && previewData.venue && (
            <NewsLocation>
              <FiMapPin />
              <span>
                {previewData.venue}, {previewData.location}
              </span>
            </NewsLocation>
          )}

          {previewData.time && (
            <NewsTime>
              <FiClock />
              <span>
                {formatNewsDate(previewData.date)} u{" "}
                {previewData.time.slice(0, 5)}
              </span>
            </NewsTime>
          )}

          <NewsExcerpt>
            {previewData.text || "Unesite sadržaj novosti..."}
          </NewsExcerpt>

          {(previewData.ticketLink || previewData.externalLink) && (
            <NewsFooter>
              <NewsActions>
                {previewData.externalLink && (
                  <ActionBtn $primary>
                    <FiExternalLink />
                    Opširnije
                  </ActionBtn>
                )}

                {previewData.ticketLink && (
                  <ActionBtn>
                    <FaTicketAlt />
                    Karte
                  </ActionBtn>
                )}

                <ActionBtn title="Podeli">
                  <FiShare2 />
                </ActionBtn>
              </NewsActions>
            </NewsFooter>
          )}
        </NewsContent>
      </PreviewNewsCard>
    );
  };

  // Render loading state
  if (loading && !newsData.length) {
    return (
      <AdminContainer>
        <PageSection>
          <PageHeader>
            <PageTitle>Upravljanje Novostima</PageTitle>
          </PageHeader>
          <Loader title={"novosti"} />
        </PageSection>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <PageSection>
        <PageHeader>
          <div>
            <PageTitle>Upravljanje Novostima</PageTitle>
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder="Pretraži novosti..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchContainer>
          </div>

          <div
            style={{
              display: "flex",
              gap: "1rem",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Button
              variant="primary"
              onClick={() => openModal("addNews")}
              disabled={createLoading}
            >
              <FiPlus />
              {createLoading ? "Dodajem..." : "Brza novost"}
            </Button>

            <Button
              variant="secondary"
              onClick={() => navigate("/admin-panel/novost/new")}
              style={{
                background: "#27ae60",
                borderColor: "#27ae60",
              }}
            >
              <FiExternalLink />
              Detaljno kreiranje
            </Button>
          </div>
        </PageHeader>

        <FilterTabs>
          <FilterTab
            $active={newsFilter === "all"}
            onClick={() => setNewsFilter("all")}
          >
            Sve novosti ({newsData.length})
          </FilterTab>
          <FilterTab
            $active={newsFilter === "active"}
            onClick={() => setNewsFilter("active")}
          >
            Objavljene ({newsData.filter((n) => n.active).length})
          </FilterTab>
          <FilterTab
            $active={newsFilter === "draft"}
            onClick={() => setNewsFilter("draft")}
          >
            Nacrti ({newsData.filter((n) => !n.active).length})
          </FilterTab>
          <FilterTab
            $active={newsFilter === "featured"}
            onClick={() => setNewsFilter("featured")}
          >
            Istaknute ({newsData.filter((n) => n.isFeatured).length})
          </FilterTab>
        </FilterTabs>

        {filteredNews.length === 0 && !loading ? (
          <EmptyState>
            <h3>{searchTerm ? "Nema rezultata" : "Nema novosti"}</h3>
            <p>
              {searchTerm
                ? `Nema novosti koji odgovaraju pretrazi "${searchTerm}"`
                : "Dodajte prvu novost klikom na dugme 'Dodaj Novu Novost'"}
            </p>
          </EmptyState>
        ) : (
          <>
            <ProductGrid>
              {filteredNews.map((newsItem) => {
                const TypeIcon = getNewsTypeIcon(newsItem.type);
                return (
                  <NewsCard key={newsItem.id} $published={newsItem.active}>
                    <NewsHeader>
                      <BadgeContainer>
                        <StatusBadge
                          status={newsItem.active ? "active" : "inactive"}
                        >
                          {newsItem.active ? "Objavljeno" : "Nacrt"}
                        </StatusBadge>
                        {newsItem.isFeatured && (
                          <StatusBadge
                            status="completed"
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 5,
                            }}
                          >
                            <FiStar size={12} />
                            Istaknuto
                          </StatusBadge>
                        )}
                      </BadgeContainer>
                      <AdminNewsTitle>{newsItem.title}</AdminNewsTitle>
                    </NewsHeader>

                    <AdminNewsExcerpt>{newsItem.text}</AdminNewsExcerpt>

                    <NewsMeta>
                      <AdminNewsType>
                        <TypeIcon size={14} />
                        {getNewsTypeLabel(newsItem.type)}
                      </AdminNewsType>

                      <NewsBand>
                        <FiMusic size={14} />
                        {newsItem?.band?.name || "N/A"}
                      </NewsBand>

                      <AdminNewsDate style={{ marginBottom: -3 }}>
                        <FiCalendar size={14} style={{ marginTop: -3 }} />
                        {formatNewsDate(newsItem.date)}
                      </AdminNewsDate>
                    </NewsMeta>

                    {newsItem.location && newsItem.venue && (
                      <Time>
                        <FiMapPin size={14} />
                        {newsItem.venue}, {newsItem.location}
                      </Time>
                    )}

                    {newsItem.time && (
                      <Time>
                        <FiClock size={14} />
                        {newsItem.time.slice(0, 5)}
                      </Time>
                    )}

                    <ProductActions>
                      <Button
                        variant={newsItem.active ? "danger" : "success"}
                        onClick={() =>
                          toggleNewsStatusHandler(newsItem.id, newsItem.active)
                        }
                        title={
                          newsItem.active
                            ? "Ukloni iz objavljenjih novosti"
                            : "Objavi novost"
                        }
                        disabled={toggleLoading}
                      >
                        {newsItem.active ? "Ukloni" : "Objavi"}
                      </Button>

                      <Button
                        variant="secondary"
                        onClick={() => openEditNewsModal(newsItem)}
                        disabled={editLoading}
                        title="Brzo uređivanje"
                      >
                        <FiEdit size={16} />
                      </Button>

                      <Button
                        variant="primary"
                        onClick={() =>
                          navigate(`/admin-panel/novost/${newsItem.id}`)
                        }
                        title="Detaljno uređivanje sa HTML editor-om"
                        style={{
                          background: "#27ae60",
                          fontSize: "0.8rem",
                          padding: "0.5rem",
                        }}
                      >
                        <FiExternalLink size={14} />
                        Detaljno
                      </Button>

                      <Button
                        variant="danger"
                        onClick={() => {
                          setNewsId(newsItem.id);
                          setDeleteNewsModal(true);
                        }}
                        title="Brisanje novosti"
                        disabled={deleteLoading}
                      >
                        <FiTrash2 size={16} />
                      </Button>
                    </ProductActions>
                  </NewsCard>
                );
              })}
            </ProductGrid>

            {/* Pagination */}
            {totalPages > 1 && (
              <PaginationContainer>
                <PaginationInfo>
                  Prikazano {(currentPage - 1) * ITEMS_PER_PAGE + 1} -{" "}
                  {Math.min(currentPage * ITEMS_PER_PAGE, total)} od {total}{" "}
                  novosti
                </PaginationInfo>

                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    alignItems: "center",
                  }}
                >
                  {/* Previous button */}
                  <PaginationButton
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1 || loading}
                    title="Prethodna stranica"
                  >
                    <FiChevronLeft />
                  </PaginationButton>

                  {/* Page numbers */}
                  {getPageNumbers().map((pageNum, index) => (
                    <PaginationButton
                      key={index}
                      onClick={() =>
                        typeof pageNum === "number"
                          ? handlePageChange(pageNum)
                          : null
                      }
                      $active={pageNum === currentPage}
                      disabled={loading || pageNum === "..."}
                      style={{
                        cursor: pageNum === "..." ? "default" : "pointer",
                        minWidth: pageNum === "..." ? "auto" : "40px",
                      }}
                    >
                      {pageNum}
                    </PaginationButton>
                  ))}

                  {/* Next button */}
                  <PaginationButton
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages || loading}
                    title="Sledeća stranica"
                  >
                    <FiChevronRight />
                  </PaginationButton>
                </div>

                <div
                  style={{
                    minWidth: "120px",
                    textAlign: "right",
                    color: "#999",
                    fontSize: "0.9rem",
                  }}
                >
                  Stranica {currentPage} od {totalPages}
                </div>
              </PaginationContainer>
            )}
          </>
        )}
      </PageSection>

      <Modal $active={activeModal === "addNews" || activeModal === "editNews"}>
        <ModalContent style={{ maxWidth: "1200px", width: "95%" }}>
          <ModalHeader>
            <ModalTitle>
              {activeModal === "editNews"
                ? "Izmeni Novosti"
                : "Dodaj Novu Novosti"}
            </ModalTitle>
            <ModalClose onClick={closeModal}>
              <FiX />
            </ModalClose>
          </ModalHeader>

          <ModalGrid>
            <FormColumn>
              <form onSubmit={handleNewsSubmit}>
                {/* Image Upload Section */}
                <FormGroup>
                  <FormLabel>Slika novosti</FormLabel>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                    }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: "none" }}
                      id="news-image-upload"
                    />

                    {newsImagePreview ? (
                      <div
                        style={{
                          position: "relative",
                          width: "200px",
                          height: "150px",
                          border: "2px dashed #ff6b35",
                          borderRadius: "8px",
                          overflow: "hidden",
                        }}
                      >
                        <img
                          src={newsImagePreview}
                          alt="News preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <button
                          type="button"
                          onClick={removeImage}
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "5px",
                            background: "#ff4444",
                            color: "white",
                            border: "none",
                            borderRadius: "50%",
                            width: "24px",
                            height: "24px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FiX size={14} />
                        </button>
                      </div>
                    ) : (
                      <label
                        htmlFor="news-image-upload"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "200px",
                          backgroundColor: "transparent",
                          height: "150px",
                          border: "2px dashed #ff6b35",
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <FiPlus
                          size={24}
                          style={{ marginBottom: "8px", color: "#ff6b35" }}
                        />
                        <span
                          style={{
                            color: "#fff",
                            fontSize: "14px",
                            backgroundColor: "transparent",
                          }}
                        >
                          Dodaj sliku
                        </span>
                        <span
                          style={{
                            color: "#999",
                            fontSize: "12px",
                            backgroundColor: "transparent",
                          }}
                        >
                          JPG, PNG, WebP (max 5MB)
                        </span>
                      </label>
                    )}
                  </div>
                </FormGroup>

                <FormGroup>
                  <FormLabel>Naslov Novosti</FormLabel>
                  <FormInput
                    type="text"
                    name="newsTitle"
                    value={previewData.title}
                    onChange={handleFormChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel>Bend</FormLabel>
                  <Relative>
                    <FormSelect
                      name="newsBand"
                      value={previewData.bandId} // Fixed: use bandId
                      onChange={handleFormChange}
                      required
                      disabled={bandsLoading}
                    >
                      <option value="">
                        {bandsLoading
                          ? "Učitavam bendove..."
                          : "Izaberite bend..."}
                      </option>
                      {bandsData.map((band) => (
                        <option key={band.id} value={band.id}>
                          {band.name}
                        </option>
                      ))}
                    </FormSelect>
                    <FiChevronDown
                      color="#ff6b35"
                      size={20}
                      style={{
                        position: "absolute",
                        right: 15,
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    />
                  </Relative>
                  {bandsError && (
                    <div
                      style={{
                        color: "#e74c3c",
                        fontSize: "0.8rem",
                        marginTop: "0.25rem",
                      }}
                    >
                      Greška pri učitavanju bendova
                    </div>
                  )}
                </FormGroup>

                <FormGroup>
                  <FormLabel>Tip Novosti</FormLabel>
                  <Relative>
                    <FormSelect
                      name="newsType"
                      value={previewData.type}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Izaberite tip...</option>
                      <option value="concert">Koncert</option>
                      <option value="album">Album</option>
                      <option value="interview">Intervju</option>
                      <option value="video">Video</option>
                      <option value="award">Nagrada</option>
                    </FormSelect>
                    <FiChevronDown
                      color="#ff6b35"
                      size={20}
                      style={{
                        position: "absolute",
                        right: 15,
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    />
                  </Relative>
                </FormGroup>

                <FormGroup>
                  <FormLabel>Sadržaj Novosti</FormLabel>
                  <FormTextarea
                    name="newsText"
                    value={previewData.text}
                    onChange={handleFormChange}
                    required
                  />
                </FormGroup>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "1rem",
                  }}
                >
                  <FormGroup>
                    <FormLabel>Grad (za koncerte)</FormLabel>
                    <FormInput
                      type="text"
                      name="newsLocation"
                      value={previewData.location}
                      onChange={handleFormChange}
                      placeholder="npr. Beograd"
                    />
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>Mesto održavanja (za koncerte)</FormLabel>
                    <FormInput
                      type="text"
                      name="newsVenue"
                      value={previewData.venue}
                      onChange={handleFormChange}
                      placeholder="npr. Stark Arena"
                    />
                  </FormGroup>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "1rem",
                  }}
                >
                  <FormGroup>
                    <FormLabel>Datum (za koncerte)</FormLabel>
                    <Relative>
                      <FormInput
                        type="date"
                        name="newsDate"
                        value={previewData.date}
                        onChange={handleFormChange}
                      />
                      <FaRegCalendarAlt
                        size={16}
                        color={"#ff6b35"}
                        style={{
                          position: "absolute",
                          right: ".8rem",
                          top: "50%",
                          transform: "translateY(-50%)",
                        }}
                      />
                    </Relative>
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Vreme (za koncerte)</FormLabel>
                    <Relative>
                      <FormInput
                        type="time"
                        name="newsTime"
                        value={previewData.time}
                        onChange={handleFormChange}
                      />
                      <MdAccessTimeFilled
                        size={16}
                        color={"#ff6b35"}
                        style={{
                          position: "absolute",
                          right: ".8rem",
                          top: "50%",
                          transform: "translateY(-50%)",
                        }}
                      />
                    </Relative>
                  </FormGroup>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "1rem",
                  }}
                >
                  <FormGroup>
                    <FormLabel>Link za karte (za koncerte)</FormLabel>
                    <FormInput
                      type="url"
                      name="ticketLink"
                      value={previewData.ticketLink}
                      onChange={handleFormChange}
                      placeholder="https://tickets.rs/..."
                    />
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>Link ka članku</FormLabel>
                    <FormInput
                      type="url"
                      name="newsExternalLink"
                      value={previewData.externalLink}
                      onChange={handleFormChange}
                      placeholder="https://..."
                    />
                  </FormGroup>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "1rem",
                  }}
                >
                  <FormGroup>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        color: "#ff6b35",
                      }}
                    >
                      <input
                        type="checkbox"
                        name="newsActive"
                        checked={previewData.active}
                        onChange={handleFormChange}
                      />
                      Objavi odmah
                    </label>
                  </FormGroup>

                  <FormGroup>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        color: "#ff6b35",
                      }}
                    >
                      <input
                        type="checkbox"
                        name="newsIsFeatured"
                        checked={previewData.isFeatured}
                        onChange={handleFormChange}
                      />
                      Istakni novost (Featured)
                    </label>
                  </FormGroup>
                </div>

                <div
                  style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}
                >
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={createLoading || editLoading}
                  >
                    {createLoading || editLoading
                      ? "Čuvam..."
                      : activeModal === "editNews"
                      ? "Sačuvaj Izmene"
                      : "Dodaj Novost"}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={closeModal}
                    disabled={createLoading || editLoading}
                  >
                    Otkaži
                  </Button>
                </div>
              </form>
            </FormColumn>

            <div>
              <PreviewSection>
                <PreviewTitle>Preview - Kako će novost izgledati:</PreviewTitle>
                <PreviewContainer>{renderPreview()}</PreviewContainer>
              </PreviewSection>
            </div>
          </ModalGrid>
        </ModalContent>
      </Modal>

      <AnimatePresence>
        {deleteNewsModal && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              if (!deleteLoading) {
                setDeleteNewsModal(false);
              }
            }}
          >
            <ConfirmBox
              as={motion.div}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn
                onClick={() => {
                  if (!deleteLoading) {
                    setDeleteNewsModal(false);
                  }
                }}
              >
                <FaTimes />
              </CloseBtn>
              <h3>Potvrdi brisanje</h3>
              <p>Da li si siguran da želiš da obrišeš ovu novost?</p>
              <ModalActions>
                <Cancel
                  onClick={() => {
                    if (!deleteLoading) {
                      setDeleteNewsModal(false);
                    }
                  }}
                  disabled={deleteLoading}
                >
                  Otkaži
                </Cancel>
                <Confirm
                  onClick={() => {
                    dispatch(deleteNews(newsId))
                      .unwrap()
                      .then(() => {
                        setDeleteNewsModal(false);
                        toast.success("Uspešno ste obrisali novost.");

                        // Refresh current page after delete
                        const remainingItems = total - 1;
                        const maxPagesAfterDelete = Math.ceil(
                          remainingItems / ITEMS_PER_PAGE
                        );
                        const targetPage =
                          currentPage > maxPagesAfterDelete
                            ? Math.max(1, maxPagesAfterDelete)
                            : currentPage;
                        loadNews(targetPage);
                      })
                      .catch((err) => toast.error(err.message));
                  }}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? "Brišem..." : "Obriši"}
                </Confirm>
              </ModalActions>
            </ConfirmBox>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </AdminContainer>
  );
};

export default AdminNewsPage;

// Preview komponente - isti stilovi kao ranije
const PreviewNewsCard = styled.article`
  background: linear-gradient(135deg, #2c2c2c 0%, #1a1a1a 100%);
  border-radius: 15px;
  overflow: hidden;
  border: 1px solid #333;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  height: fit-content;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(255, 107, 53, 0.2);
    border-color: #ff6b35;
  }
`;

const NewsImage = styled.div`
  height: 200px;

  width: 100%;
  background: ${({ $backgroundImage }) =>
    $backgroundImage
      ? `linear-gradient(
          135deg, 
          rgba(26, 26, 26, 0.3) 0%, 
          rgba(42, 42, 42, 0.35) 50%,
          rgba(26, 26, 26, 0.3) 100%
        ), 
        url(${$backgroundImage})`
      : "linear-gradient(45deg, #333, #555, #ff6b35)"};

  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  background-attachment: fixed;

  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 107, 53, 0.3),
      rgba(0, 0, 0, 0.6)
    );
  }
`;

const NewsDate = styled.div`
  position: absolute;
  top: 15px;
  left: 15px;
  background: rgba(255, 107, 53, 0.95);
  color: #ffffff;
  padding: 0.5rem 0.8rem;
  border-radius: 8px;
  font-weight: bold;
  z-index: 3;
  font-size: 0.8rem;
`;

const NewsType = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  background: ${({ type }) =>
    type === "concert"
      ? "#27ae60"
      : type === "album"
      ? "#9b59b6"
      : type === "interview"
      ? "#3498db"
      : type === "video"
      ? "#e74c3c"
      : type === "award"
      ? "#f39c12"
      : "#ff6b35"};
  color: #ffffff;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: bold;
  z-index: 3;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const NewsContent = styled.div`
  padding: 1.5rem;
`;

const BandTag = styled.span`
  background: #ff6b35;
  color: #ffffff;
  padding: 0.2rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: bold;
  display: inline-block;
  margin-bottom: 0.8rem;
`;

const NewsTitle = styled.h2`
  color: #ffffff;
  font-size: 1.3rem;
  margin-bottom: 0.8rem;
  font-weight: bold;
  line-height: 1.3;
`;

const NewsExcerpt = styled.p`
  color: #cccccc;
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const NewsLocation = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #aaa;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;

  svg {
    color: #ff6b35;
  }
`;

const NewsTime = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #aaa;
  margin-bottom: 1rem;
  font-size: 0.9rem;

  svg {
    color: #ff6b35;
  }
`;

const NewsFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid #333;
`;

const NewsActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionBtn = styled.button`
  background: ${({ $primary }) => ($primary ? "#ff6b35" : "transparent")};
  color: ${({ $primary }) => ($primary ? "#ffffff" : "#ff6b35")};
  border: 2px solid #ff6b35;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: bold;
  font-size: 0.9rem;

  &:hover {
    background: #ff6b35;
    color: #ffffff;
    transform: translateY(-1px);
  }
`;

// Styled komponente za preview sekciju
const PreviewSection = styled.div`
  background: #0a0a0a;
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #333;
`;

const PreviewTitle = styled.h4`
  color: #ff6b35;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  font-weight: bold;
`;

const PreviewContainer = styled.div`
  max-width: 400px;
  margin: 0 auto;
`;

const ModalGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const FormColumn = styled.div`
  min-width: 0;
`;
